import { navigateTo } from '#imports'
import { type Ref, readonly, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { noop } from '@backmarket/utils/function/noop'
import { closeModal, openModal } from '@ds/components/ModalBase'

import headerTranslations from '../pages/components/Header/Header.translations'

import type {
  NewChatDialog,
  WebShoppingAssistantHost,
} from './useShoppingAssistantHost.injection-key'

// Single RevDialog instance shared by both the new-chat and close confirmations.
export const WEB_PAGE_CONFIRM_DIALOG_NAME =
  'shopping-assistant-web-page-confirm'

/**
 * Builds the host adapter for the standalone /assistant web page. Close and
 * new-chat are confirmed via a RevDialog (gated on a non-empty conversation),
 * close navigates back to the homepage (there is no panel to hide — this is a
 * full page), and cart/url/ready concerns are no-ops on the web (the
 * front-office cart updates reactively in-app; there is no chat URL in v1).
 *
 * Returns the host plus the bits the container component needs to render the
 * confirmation dialog and to inject a message into a live conversation.
 */
export function useWebPageShoppingAssistantHost(): {
  host: WebShoppingAssistantHost
  currentDialog: Ref<NewChatDialog | null>
  acceptDialog: () => void
  declineDialog: () => void
} {
  const i18n = useI18n()

  const receivedEvent = ref('')
  const hasUserMessages = ref(false)
  const currentDialog = ref<NewChatDialog | null>(null)

  function confirm(dialog: NewChatDialog) {
    currentDialog.value = dialog
    openModal(WEB_PAGE_CONFIRM_DIALOG_NAME)
  }

  function acceptDialog() {
    const dialog = currentDialog.value
    closeModal(WEB_PAGE_CONFIRM_DIALOG_NAME)
    currentDialog.value = null
    dialog?.onAccept()
  }

  function declineDialog() {
    const dialog = currentDialog.value
    closeModal(WEB_PAGE_CONFIRM_DIALOG_NAME)
    currentDialog.value = null
    dialog?.onDecline?.()
  }

  const host: WebShoppingAssistantHost = {
    hostType: 'web-page',
    receivedEvent: readonly(receivedEvent),
    clearReceivedEvent: () => {
      receivedEvent.value = ''
    },
    close() {
      if (hasUserMessages.value) {
        confirm({
          title: i18n(headerTranslations.newConversationDialogTitle),
          description: i18n(
            headerTranslations.newConversationDialogDescription,
          ),
          acceptButtonText: i18n(headerTranslations.closeChat),
          declineButtonText: i18n(
            headerTranslations.newConversationDialogNegativeButton,
          ),
          onAccept: () => navigateTo('/'),
        })

        return
      }

      navigateTo('/')
    },
    newChat(dialog) {
      // Plus button: confirm only when there's a real conversation to lose.
      if (hasUserMessages.value) {
        confirm(dialog)

        return
      }

      dialog.onAccept()
    },
    reportConversation: (value: boolean) => {
      hasUserMessages.value = value
    },
    // Live browsing context is sidebar-only — this full-page host
    // has no navigation to track context across, so this is a no-op.
    syncChatId: noop,
  }

  return { host, currentDialog, acceptDialog, declineDialog }
}
